/**
 * Metronome clicks for Strum Machine (Web Audio), aligned to 8th-note grid (strumIdx 0–7).
 */

export type StrumMetronomeStyle =
  | 'none'
  | 'classic'
  | 'quarters'
  | 'downbeat'
  | 'wood'
  | 'digital'
  | 'hihat';

export const STRUM_METRONOME_OPTIONS: { value: StrumMetronomeStyle; label: string }[] = [
  { value: 'none', label: 'Off (strums only)' },
  { value: 'classic', label: 'Classic — every 8th, accent beat 1' },
  { value: 'quarters', label: 'Quarter notes — beats 1–4' },
  { value: 'downbeat', label: 'Downbeat only — beat 1' },
  { value: 'wood', label: 'Woodblock — same timing as classic' },
  { value: 'digital', label: 'Digital beep — same timing as classic' },
  { value: 'hihat', label: 'Hi-hat — light 8ths' },
];

/** 0 = silent, 1 = soft, 2 = medium, 3 = strong (downbeat) */
function clickLevel(
  pattern: 'eighths' | 'quarters' | 'downbeat',
  strumIdx: number
): 0 | 1 | 2 | 3 {
  const onQuarter = strumIdx % 2 === 0;
  const downbeat = strumIdx === 0;

  if (pattern === 'downbeat') return downbeat ? 3 : 0;
  if (pattern === 'quarters') {
    if (!onQuarter) return 0;
    return downbeat ? 3 : 2;
  }
  // eighths
  if (downbeat) return 3;
  if (onQuarter) return 2;
  return 1;
}

function connectEnvelope(
  ctx: AudioContext,
  gain: GainNode,
  start: number,
  peak: number,
  duration: number
) {
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + Math.min(0.004, duration * 0.25));
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
}

function resolvePatternAndTimbre(style: StrumMetronomeStyle): {
  pattern: 'eighths' | 'quarters' | 'downbeat';
  timbre: 'classic' | 'wood' | 'digital' | 'hihat';
} {
  switch (style) {
    case 'none':
      return { pattern: 'eighths', timbre: 'classic' };
    case 'classic':
      return { pattern: 'eighths', timbre: 'classic' };
    case 'wood':
      return { pattern: 'eighths', timbre: 'wood' };
    case 'digital':
      return { pattern: 'eighths', timbre: 'digital' };
    case 'hihat':
      return { pattern: 'eighths', timbre: 'hihat' };
    case 'quarters':
      return { pattern: 'quarters', timbre: 'classic' };
    case 'downbeat':
      return { pattern: 'downbeat', timbre: 'classic' };
    default:
      return { pattern: 'eighths', timbre: 'classic' };
  }
}

export function playStrumMetronomeTick(
  ctx: AudioContext,
  when: number,
  strumIdx: number,
  style: StrumMetronomeStyle
): void {
  if (style === 'none') return;

  const { pattern, timbre } = resolvePatternAndTimbre(style);
  const level = clickLevel(pattern, strumIdx);
  if (level === 0) return;

  const peak = level === 3 ? 0.42 : level === 2 ? 0.28 : 0.16;

  if (timbre === 'hihat') {
    const len = ctx.sampleRate * 0.04;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const t = i / len;
      d[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 6000;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 9000;
    bp.Q.value = 0.4;
    const g = ctx.createGain();
    const scale = level === 1 ? 0.55 : 1;
    connectEnvelope(ctx, g, when, peak * 0.55 * scale, 0.045);
    src.connect(hp);
    hp.connect(bp);
    bp.connect(g);
    g.connect(ctx.destination);
    src.start(when);
    src.stop(when + 0.06);
    return;
  }

  if (timbre === 'digital') {
    const osc = ctx.createOscillator();
    osc.type = 'square';
    const f = level === 3 ? 990 : level === 2 ? 770 : 620;
    osc.frequency.setValueAtTime(f, when);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 4200;
    lp.Q.value = 0.7;
    const g = ctx.createGain();
    connectEnvelope(ctx, g, when, peak * 0.35, 0.035);
    osc.connect(lp);
    lp.connect(g);
    g.connect(ctx.destination);
    osc.start(when);
    osc.stop(when + 0.05);
    return;
  }

  if (timbre === 'wood') {
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    osc1.type = 'triangle';
    osc2.type = 'sine';
    const f1 = level === 3 ? 380 : level === 2 ? 320 : 260;
    const f2 = f1 * 2.1;
    osc1.frequency.setValueAtTime(f1, when);
    osc2.frequency.setValueAtTime(f2, when);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 1.2;
    const g1 = ctx.createGain();
    const g2 = ctx.createGain();
    const master = ctx.createGain();
    g1.gain.value = 0.55;
    g2.gain.value = 0.35;
    connectEnvelope(ctx, master, when, peak, 0.022);
    osc1.connect(g1);
    osc2.connect(g2);
    g1.connect(bp);
    g2.connect(bp);
    bp.connect(master);
    master.connect(ctx.destination);
    osc1.start(when);
    osc2.start(when);
    osc1.stop(when + 0.04);
    osc2.stop(when + 0.04);
    return;
  }

  // classic (default timbre): triangle + sine + tiny noise, similar to standalone metronome
  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  osc1.type = 'triangle';
  osc2.type = 'sine';
  const f1 = level === 3 ? 1000 : level === 2 ? 700 : 550;
  const f2 = level === 3 ? 2000 : level === 2 ? 1400 : 1100;
  osc1.frequency.setValueAtTime(f1, when);
  osc2.frequency.setValueAtTime(f2, when);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = level === 3 ? 3500 : 2800;
  bp.Q.value = 0.5;
  const g1 = ctx.createGain();
  const g2 = ctx.createGain();
  const master = ctx.createGain();
  g1.gain.value = 0.55;
  g2.gain.value = 0.32;
  connectEnvelope(ctx, master, when, peak, 0.018);
  osc1.connect(g1);
  osc2.connect(g2);
  g1.connect(bp);
  g2.connect(bp);
  bp.connect(master);
  master.connect(ctx.destination);
  osc1.start(when);
  osc2.start(when);
  osc1.stop(when + 0.035);
  osc2.stop(when + 0.035);
}
